import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Breakpoint } from '@travel-frontend/core';
import { IconAdd, IconClose } from '@travel-frontend/icons';
import { range } from 'lodash';
import { Trans, useTranslation } from 'next-i18next';
import { forwardRef, useMemo } from 'react';
import { Content, Headline, HotlineWidget, LinkButton, SelectContainer } from '../../../../components/SearchForm/components/TravellersWidgetMultiroom/components/index.js';
import { WidgetChrome } from '../../../../components/SearchForm/components/TravellersWidgetMultiroom/components/WidgetChrome.js';
import { WidgetOverlay } from '../../../../components/SearchForm/components/WidgetOverlay/index.js';
import { Alert, Button, Divider, LabelMedium, LabelMediumBold, LabelSmallBold, LabelXSmall, Notice, Select, Stack, Stepper } from '../../../../primitives/index.js';
import { qaId } from '../../../../utils/index.js';
import { SEARCH_FORM_QA_ID } from '../../../../utils/qaId.js';
import { SPACING_NONE } from '../../../../utils/spacingLegacy.js';
export const TravellersWidgetMultiroom = forwardRef(function TravellersWidgetMultiroom({
  type,
  open,
  data,
  hasMissingChildrenAgeMessage,
  childrenAgesValidationState,
  applyButtonDisabled,
  handlers,
  config,
  widgetHead,
  dimming = false,
  ...restProps
}, ref) {
  const {
    t
  } = useTranslation(['travellersWidgetMultiroom']);
  const {
    onApply,
    onClose,
    onReset,
    onAdultsCountChange,
    onChildAgeChange,
    onChildrenCountChange,
    onRoomAdd,
    onRoomRemove
  } = handlers;
  const {
    adultsCountMin,
    adultsCountMax,
    childrenCountMin,
    childrenCountMax,
    childrenAgeMin,
    childrenAgeMax,
    travellersCountMax,
    roomsCountMax,
    serviceConfig
  } = config;
  const childrenAgeOptions = useMemo(() => range(childrenAgeMin, childrenAgeMax).map(index => ({
    value: index,
    label: index === 1 ? t('childrenAgeOptions.optionInfant', {
      age: 2
    }) : t('childrenAgeOptions.optionChild', {
      age: index
    })
  })), [childrenAgeMin, childrenAgeMax, t]);
  const getActiveSelectOption = (roomIndex, childIndex) => {
    return Array.isArray(data.rooms[roomIndex].childrenAges) && data.rooms[roomIndex].childrenAges[childIndex] != null ? childrenAgeOptions.find(option => option.value === data.rooms[roomIndex].childrenAges[childIndex]) : null;
  };
  const maxTravellersReached = data.adultsCount + data.childrenCount >= travellersCountMax;
  const showMultiRoomHotline = maxTravellersReached || data.rooms.length >= roomsCountMax;
  return _jsx(WidgetOverlay, {
    dim: dimming,
    type: type,
    open: open,
    children: _jsx(WidgetChrome, {
      ...restProps,
      ref: ref,
      type: type,
      head: widgetHead,
      title: t('title.dialog'),
      onClose: onClose,
      asideRight: serviceConfig && _jsx(HotlineWidget, {
        serviceConfig: serviceConfig,
        multipleRooms: data.rooms.length > 1,
        maxTravellersOrRoomsReached: showMultiRoomHotline
      }),
      controls: [_jsx(Button, {
        onClick: onReset,
        category: "secondary",
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-reset-button'),
        children: t('button.reset')
      }, "reset-button"), _jsx(Button, {
        onClick: onApply,
        deactivated: applyButtonDisabled,
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-apply-button'),
        children: t('button.apply')
      }, "apply-button")],
      "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-multiroom'),
      children: _jsxs(Content, {
        children: [_jsx(Headline, {
          children: t('title.content')
        }), _jsx(Stack, {
          divider: true,
          children: data.rooms.map(({
            adultsCount,
            childrenAges
          }, roomIndex) => {
            const childrenCount = childrenAges.length;
            return _jsxs(Stack, {
              "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-single-room'),
              children: [_jsxs(Stack, {
                direction: "row",
                alignItems: "center",
                justifyContent: "space-between",
                "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-room-header'),
                children: [_jsx(LabelSmallBold, {
                  "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-room-number'),
                  children: t('title.room', {
                    number: roomIndex + 1
                  })
                }), data.rooms.length > 1 && _jsx(LinkButton, {
                  variant: "interaction",
                  category: "tertiary",
                  onClick: () => onRoomRemove(roomIndex),
                  style: {
                    fontWeight: 400
                  },
                  "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-room-remove-button'),
                  children: _jsxs(Stack, {
                    direction: "row",
                    alignItems: "center",
                    spacing: SPACING_NONE,
                    children: [_jsx(IconClose, {}), " ", t('rooms.removeRoom')]
                  })
                })]
              }), _jsxs(Stack, {
                spacing: 1.25,
                children: [_jsxs(Stack, {
                  direction: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  children: [_jsx(LabelMedium, {
                    children: t('stepper.adultsLabel')
                  }), _jsx(Stepper, {
                    style: {
                      fontWeight: 700
                    },
                    "data-testid": "adult-count",
                    disableIncrement: maxTravellersReached,
                    min: adultsCountMin,
                    max: adultsCountMax,
                    value: adultsCount,
                    onChange: amount => {
                      onAdultsCountChange(roomIndex, amount);
                    }
                  })]
                }), _jsxs(Stack, {
                  direction: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  children: [_jsxs(LabelMedium, {
                    children: [t('stepper.childrenLabel'), _jsx(LabelXSmall, {
                      color: "contentSecondary",
                      children: t('stepper.childrenLabelHint', {
                        min: childrenAgeMin - 1,
                        max: childrenAgeMax - 1
                      })
                    })]
                  }), _jsx(Stepper, {
                    style: {
                      fontWeight: 700
                    },
                    "data-testid": "children-count",
                    disableIncrement: maxTravellersReached,
                    min: childrenCountMin,
                    max: childrenCountMax,
                    value: childrenCount,
                    onChange: count => onChildrenCountChange(roomIndex, count)
                  })]
                }), childrenCount > 0 && _jsxs(Stack, {
                  "data-testid": "children-ages",
                  children: [hasMissingChildrenAgeMessage && childrenAgesValidationState[roomIndex].includes(false) && _jsx(Alert, {
                    variant: "warning",
                    caption: t('alert.missingChildrenAge.caption', {
                      count: childrenCount
                    })
                  }), _jsx(LabelSmallBold, {
                    as: "span",
                    children: t('childrenAgeOptions.caption')
                  }), range(childrenCount).map(childIndex => _jsx(Select, {
                    // @ts-expect-error ...
                    className: "travellers-multiroom-widget",
                    customMenuListHeight: '200px',
                    menuPortalElement: true,
                    options: childrenAgeOptions,
                    isSearchable: false,
                    value: getActiveSelectOption(roomIndex, childIndex),
                    onChange: option => {
                      onChildAgeChange(roomIndex, childIndex, option.value);
                    },
                    menuPlacement: "top",
                    placeholder: _jsx(Trans, {
                      ns: "travellersWidgetMultiroom",
                      i18nKey: "childrenAgeOptions.placeholder",
                      values: {
                        nr: childIndex + 1
                      },
                      components: {
                        bold: _jsx(LabelMediumBold, {
                          as: "span"
                        })
                      }
                    }),
                    formatSingleValue: state => ({
                      value: t('childrenAgeOptions.selectedValue', {
                        nr: childIndex + 1,
                        optionLabel: state.data.label,
                        interpolation: {
                          escapeValue: false
                        }
                      })
                    }),
                    highlighted: hasMissingChildrenAgeMessage && !childrenAgesValidationState[roomIndex][childIndex],
                    components: {
                      SelectContainer
                    }
                  }, `child${childIndex}`))]
                })]
              }), roomIndex === 0 && data.rooms.length === 1 && data.adultsCount === adultsCountMax && _jsxs(Notice, {
                variant: "warning",
                children: [_jsx("b", {
                  children: t('alert.maxAdultsInOneRoom.caption', {
                    adultsCountMax
                  })
                }), _jsx("br", {}), t('alert.maxAdultsInOneRoom.content')]
              })]
            }, roomIndex);
          })
        }), _jsx(Divider, {}), maxTravellersReached && _jsx(Notice, {
          variant: "warning",
          "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-note-maxTravelers'),
          children: _jsx("b", {
            children: t('alert.maxTravellers.caption', {
              count: travellersCountMax
            })
          })
        }), data.rooms.length === roomsCountMax && _jsx(Notice, {
          variant: "warning",
          "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-note-maxRoom'),
          children: _jsx("b", {
            children: t('alert.maxRooms.caption', {
              roomsCountMax
            })
          })
        }), data.rooms.length !== roomsCountMax && _jsx(LinkButton, {
          variant: "interaction",
          category: "tertiary",
          onClick: onRoomAdd,
          disabled: maxTravellersReached,
          "data-testid": qaId(SEARCH_FORM_QA_ID, 'travellers-widget-room-add-button'),
          children: _jsxs(Stack, {
            direction: "row",
            alignItems: "center",
            spacing: SPACING_NONE,
            children: [_jsx(IconAdd, {}), " ", t('rooms.addRoom')]
          })
        }), serviceConfig && _jsx(Breakpoint, {
          matches: ['xs', 'sm', 'md'],
          children: _jsx(HotlineWidget, {
            serviceConfig: serviceConfig,
            multipleRooms: data.rooms.length > 1,
            maxTravellersOrRoomsReached: showMultiRoomHotline
          })
        })]
      })
    })
  });
});